import './ConversationList.css';
import { ConversationListItem } from 'src/pages/ConversationsPage/ConversationList/ConversationListItem';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { conversationKey, type Conversation } from 'src/pages/ConversationsPage/conversationTypes';

interface ConversationListProps {
  loadMore: () => void;
  isLoading: boolean;
  // A later page or a refetch for new filters; the list is cleared while it loads.
  isFetching?: boolean;
  isError: boolean;
  hasNextPage: boolean;
  chats?: Conversation[];
  onSelect: (chat: Conversation) => void;
}

export const ConversationList = ({
  loadMore,
  isLoading,
  isFetching = false,
  isError,
  hasNextPage,
  chats = [],
  onSelect,
}: ConversationListProps) => {
  // Loading turns off after every page, so the hook loads the next one while the end stays in view.
  const [sentryRef] = useInfiniteScroll({
    loading: isLoading || isFetching,
    hasNextPage: hasNextPage,
    onLoadMore: loadMore,
    disabled: !!isError,
    rootMargin: '0px 0px 500px 0px',
  });

  const onClick = (selectedChat: Conversation) => {
    chats.forEach((chat) => {
      chat.selected = selectedChat === chat;
    });

    onSelect(selectedChat);
  };

  const isEmpty = !chats.length && !hasNextPage && !isLoading && !isFetching && !isError;

  if (isEmpty) {
    return (
      <div className={'flex h-full flex-col items-center justify-center gap-1 p-6 text-center'}>
        <p className={'text-sm font-semibold text-ebony'}>No conversations found</p>
        <p className={'text-xs font-medium text-ebony_opacity_50'}>
          Try changing the filters or check back later.
        </p>
      </div>
    );
  }

  return (
    <ul className={'overflow-auto h-full pr-2.5'}>
      {chats.map((chat) => {
        return <ConversationListItem key={conversationKey(chat)} chat={chat} onClick={onClick} />;
      })}
      {hasNextPage && (
        <li
          ref={sentryRef}
          className={
            'py-4 px-2.5 gap-2 flex flex-row items-center justify-center text-base font-medium text-ebony_opacity_50'
          }
        >
          <div className={'scroll-loader'}></div>
          <div>Loading...</div>
        </li>
      )}
    </ul>
  );
};
